import React, { useRef, useState } from "react";
import { Camera, Upload, ScanLine, X, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import api from "@/lib/api";
import { ALL_COUNTRIES } from "@/lib/applicants";

const SEX_MAP = { M: "Male", F: "Female", X: "Other" };

// Case-insensitive lookup that also handles small variants ("UK" → "United Kingdom",
// "USA" → "United States", "Republic of India" → "India"). Falls back to null if
// the OCR-provided name has no match in the form's country list.
const COUNTRY_ALIASES = {
  "uk": "United Kingdom",
  "great britain": "United Kingdom",
  "britain": "United Kingdom",
  "usa": "United States",
  "u.s.a.": "United States",
  "united states of america": "United States",
  "republic of india": "India",
  "russian federation": "Russia",
  "korea, republic of": "South Korea",
  "korea, south": "South Korea",
  "republic of korea": "South Korea",
  "iran, islamic republic of": "Iran",
};
const _COUNTRY_INDEX = ALL_COUNTRIES.reduce((m, c) => { m[c.toLowerCase()] = c; return m; }, {});
function normalizeCountry(raw) {
  if (!raw) return null;
  const s = String(raw).trim();
  const lower = s.toLowerCase();
  if (_COUNTRY_INDEX[lower]) return _COUNTRY_INDEX[lower];
  if (COUNTRY_ALIASES[lower]) return COUNTRY_ALIASES[lower];
  // Try stripping punctuation.
  const stripped = lower.replace(/[.,]/g, "").trim();
  if (_COUNTRY_INDEX[stripped]) return _COUNTRY_INDEX[stripped];
  if (COUNTRY_ALIASES[stripped]) return COUNTRY_ALIASES[stripped];
  return null;
}

/**
 * Passport OCR upload card. Additive accelerator — the form below always
 * remains editable and this card is entirely optional.
 *
 * Props:
 *   applicantIndex: number     — used to namespace data-testid attributes
 *   onExtracted:    ({fields, expiry_notice, detected}) => void
 */
export default function PassportOcrCard({ applicantIndex = 0, onExtracted }) {
  const [state, setState] = useState("idle"); // idle | processing | error | done
  const [errorMsg, setErrorMsg] = useState("");
  const [fileName, setFileName] = useState("");
  const [choice, setChoice] = useState(null); // null | "upload" | "manual"
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const idp = `applicant-${applicantIndex}`;

  const openFilePicker = () => fileInputRef.current?.click();
  const openCameraPicker = () => cameraInputRef.current?.click();

  async function processFile(file) {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setState("error");
      setErrorMsg("File is larger than 10 MB. Please upload a smaller image.");
      return;
    }
    setFileName(file.name);
    setState("processing");
    setErrorMsg("");
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/ocr/passport", form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      if (data.detected === "not_a_passport") {
        setState("error");
        setErrorMsg("This doesn't look like a passport bio page. Please upload a clear photo of the page with your photo and MRZ (the two lines at the bottom).");
        return;
      }
      if (data.detected === "unreadable") {
        setState("error");
        setErrorMsg("The image was too blurry to read reliably. Try a sharper, well-lit photo — or enter your details manually below.");
        return;
      }
      // Build a patch containing only fields the model actually filled.
      const patch = {};
      const filledKeys = [];
      const mapping = {
        surname: "surname",
        surname_at_birth: "surname_at_birth",
        first_names: "first_names",
        date_of_birth: "date_of_birth",
        place_of_birth: "place_of_birth",
        country_of_birth: "country_of_birth",
        nationality: "nationality",
        sex: "sex",
        passport_number: "passport_number",
        passport_issue_date: "passport_issue_date",
        passport_expiry_date: "passport_expiry_date",
        issuing_country: "passport_issued_country",
        issuing_authority: "passport_issued_by_authority",
      };
      for (const [src, dst] of Object.entries(mapping)) {
        let v = data[src];
        if (v === null || v === undefined || v === "") continue;

        // Normalize values that must match the form's option lists —
        // otherwise the field would be "filled" but the <select> shows blank.
        if (src === "sex") {
          v = SEX_MAP[String(v).toUpperCase()] || null;
        } else if (src === "nationality" || src === "country_of_birth" || src === "issuing_country") {
          v = normalizeCountry(v);
        }
        if (v === null || v === undefined || v === "") continue;

        patch[dst] = v;
        filledKeys.push(dst);
      }
      // Passport number confirm mirrors passport_number on autofill (user
      // still sees the mismatch check if they edit either later).
      if (patch.passport_number) {
        patch.passport_number_confirm = patch.passport_number;
        filledKeys.push("passport_number_confirm");
      }
      if (Object.keys(patch).length === 0) {
        setState("error");
        setErrorMsg("We couldn't read anything from this image. Please try a clearer photo or enter your details manually.");
        return;
      }
      onExtracted?.({ patch, filledKeys, expiryNotice: data.expiry_notice || null });
      setState("done");
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      setState("error");
      if (status === 429) {
        setErrorMsg(typeof detail === "string" ? detail : "Too many attempts. Please try again later or enter your details manually.");
      } else if (status === 413) {
        setErrorMsg("File is larger than 10 MB. Please upload a smaller image.");
      } else if (typeof detail === "string") {
        setErrorMsg(detail);
      } else {
        setErrorMsg("Something went wrong reading the passport. Please try again or enter your details manually.");
      }
    }
  }

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) processFile(f);
    e.target.value = ""; // allow re-uploading the same file
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) processFile(f);
  };

  const reset = () => {
    setState("idle");
    setErrorMsg("");
    setFileName("");
  };

  // ---------- Render ----------
  if (choice === "manual" && state !== "done") {
    // Compact hint that OCR is still available.
    return (
      <div
        className="mb-6 rounded-lg border border-dashed border-slate-300 bg-white px-4 py-3 flex items-center justify-between text-sm text-slate-600"
        data-testid={`${idp}-ocr-manual-hint`}
      >
        <span>Filling this in by hand. Changed your mind?</span>
        <button
          type="button"
          onClick={() => setChoice(null)}
          className="text-[#2563eb] font-semibold hover:underline"
          data-testid={`${idp}-ocr-switch-to-upload`}
        >
          Upload passport photo instead
        </button>
      </div>
    );
  }

  if (state === "done") {
    return (
      <div
        className="mb-6 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 flex items-center gap-3"
        role="status"
        aria-live="polite"
        data-testid={`${idp}-ocr-done`}
      >
        <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
        <div className="flex-1 text-sm text-emerald-900">
          <div className="font-semibold">Details filled in from your passport.</div>
          <div className="text-emerald-800">Please review every field carefully before continuing — highlighted fields came from the scan.</div>
        </div>
        <button
          type="button"
          onClick={reset}
          className="text-sm text-emerald-700 hover:underline font-medium"
          data-testid={`${idp}-ocr-scan-again`}
        >
          Scan again
        </button>
      </div>
    );
  }

  return (
    <div
      className="mb-6 rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white shadow-sm overflow-hidden"
      data-testid={`${idp}-ocr-card`}
    >
      <div className="p-5 sm:p-6">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-lg bg-[#2563eb]/10 flex items-center justify-center shrink-0">
            <ScanLine className="h-5 w-5 text-[#2563eb]" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-display font-bold text-lg text-[#0f1b2d] leading-tight">
              Speed up your application
            </h3>
            <p className="text-sm text-slate-600 mt-0.5">
              Upload your passport bio page and we&apos;ll fill in the details below for you. You can always review and edit.
            </p>
          </div>
        </div>

        {state === "idle" && (
          <>
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              className={`mt-5 rounded-lg border-2 border-dashed transition-colors ${
                dragOver ? "border-[#2563eb] bg-[#2563eb]/5" : "border-slate-300 bg-white"
              }`}
              data-testid={`${idp}-ocr-dropzone`}
            >
              <div className="p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex-1 text-sm text-slate-600">
                  <div className="font-medium text-[#0f1b2d]">Drag & drop your passport photo here</div>
                  <div className="text-xs text-slate-500 mt-0.5">JPG, PNG or PDF · up to 10 MB</div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    type="button"
                    onClick={openFilePicker}
                    className="inline-flex items-center justify-center gap-2 rounded-md bg-[#2563eb] text-white px-4 py-2 text-sm font-semibold hover:bg-[#1d4ed8] transition-colors"
                    data-testid={`${idp}-ocr-upload-btn`}
                  >
                    <Upload className="h-4 w-4" />
                    Upload passport photo
                  </button>
                  <button
                    type="button"
                    onClick={openCameraPicker}
                    className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-300 bg-white text-[#0f1b2d] px-4 py-2 text-sm font-semibold hover:bg-slate-50 transition-colors sm:hidden"
                    data-testid={`${idp}-ocr-camera-btn`}
                  >
                    <Camera className="h-4 w-4" />
                    Use camera
                  </button>
                </div>
              </div>
            </div>
            <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs text-slate-500">
              <span>Your passport image is used only to read your details and is not stored.</span>
              <button
                type="button"
                onClick={() => setChoice("manual")}
                className="text-[#2563eb] font-semibold hover:underline text-left sm:text-right"
                data-testid={`${idp}-ocr-enter-manually`}
              >
                Enter manually →
              </button>
            </div>
          </>
        )}

        {state === "processing" && (
          <div
            className="mt-5 rounded-lg border border-slate-200 bg-white p-5"
            role="status"
            aria-live="polite"
            data-testid={`${idp}-ocr-processing`}
          >
            <div className="flex items-center gap-3">
              <Loader2 className="h-5 w-5 animate-spin text-[#2563eb]" />
              <div className="flex-1">
                <div className="font-medium text-[#0f1b2d] text-sm">Reading your passport…</div>
                <div className="text-xs text-slate-500 mt-0.5 truncate">{fileName}</div>
              </div>
            </div>
            <div className="mt-3 h-1 w-full bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-[#2563eb] rounded-full animate-[ocrbar_1.4s_ease-in-out_infinite]" style={{ width: "40%" }} />
            </div>
            <style>{`@keyframes ocrbar { 0% { transform: translateX(-100%); } 100% { transform: translateX(300%); } }`}</style>
          </div>
        )}

        {state === "error" && (
          <div
            className="mt-5 rounded-lg border border-amber-200 bg-amber-50 p-4"
            role="alert"
            aria-live="assertive"
            data-testid={`${idp}-ocr-error`}
          >
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="flex-1 text-sm">
                <div className="font-semibold text-amber-900">Couldn&apos;t read the passport</div>
                <div className="text-amber-800 mt-0.5">{errorMsg}</div>
              </div>
              <button
                type="button"
                onClick={reset}
                className="text-amber-700 hover:text-amber-900 p-1"
                aria-label="Dismiss"
                data-testid={`${idp}-ocr-error-dismiss`}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={openFilePicker}
                className="inline-flex items-center gap-2 rounded-md bg-white border border-amber-300 text-amber-900 px-3 py-1.5 text-sm font-semibold hover:bg-amber-100"
                data-testid={`${idp}-ocr-error-retry`}
              >
                <Upload className="h-3.5 w-3.5" />
                Try another photo
              </button>
              <button
                type="button"
                onClick={() => { setChoice("manual"); reset(); }}
                className="inline-flex items-center gap-2 rounded-md text-amber-900 px-3 py-1.5 text-sm font-semibold hover:bg-amber-100"
                data-testid={`${idp}-ocr-error-manual`}
              >
                Enter details manually
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Hidden file inputs */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,application/pdf"
        className="hidden"
        onChange={onFileChange}
        data-testid={`${idp}-ocr-file-input`}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={onFileChange}
        data-testid={`${idp}-ocr-camera-input`}
      />
    </div>
  );
}
